import { Container, Input } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import DataWrapper from 'components/molecules/data-wrapper/DataWrapper';
import HeaderStatusWrapper from 'components/molecules/headerStatusWrapper/HeaderStatusWrapper';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useTranslation } from 'react-i18next';
import { PiMicrophone } from 'react-icons/pi';
import { useGetCopyrightList } from 'services/account';
import { useGetGenres, useGetLanguages } from 'services/enums';
import { TRACK_CENSORIOUS } from 'services/tracks/types';
import { IVideoMutationForm } from 'services/video/types';
import { getYearsSelectOptions, useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getLanguageSelectOptions } from 'utils/global/language.utils';

const VideoData = () => {
  const { t } = useTranslation();
  const { getSelectOptions } = useEnumToSelectOptions();

  const languages = useGetLanguages();

  const copyrightOptions = useGetCopyrightList();

  const genres = useGetGenres();
  const { values, setFieldValue, initialValues }: IFormikContextType<IVideoMutationForm> =
    useFormikContext<IVideoMutationForm>();

  const boolOptions = [
    { value: true, label: t(`enum.LABEL_REPAYMENTS.Yes`) },
    { value: false, label: t(`enum.LABEL_REPAYMENTS.No`) },
  ];

  return (
    <DataWrapper>
      {!!initialValues.mainTitle.title.length && (
        <HeaderStatusWrapper title={t('input.label.status')}>
          {initialValues.status && <LabeledStatus status={initialValues.status} fullWidth />}
        </HeaderStatusWrapper>
      )}
      <Container>
        <Container.Header
          title={
            <>
              <PiMicrophone /> {t('artist.tabs.data')}
            </>
          }
        />

        <Container.Content>
          <FormInput.Text name="mainTitle.title" label={`${t('input.label.title')}`} autoFocus />
          <FormInput.Text name="mainTitle.version" label={`${t('input.label.version')}`} />
          <FormInput.Select
            name="language"
            label={t('input.label.language')}
            options={getLanguageSelectOptions(languages.data, [
              ...values.otherTitles,
              values.mainTitle,
            ])}
            value={{
              value: values.mainTitle.language,
              label: t(`enum.LANGUAGE.${values.mainTitle.language}`),
            }}
            onChange={({ value }) => setFieldValue('mainTitle.language', value)}
          />

          <FormInput.Text name="isrc" label="ISRC" disabled={values.canUpdateISRC === false} />

          <FormInput.Select
            name="censorious"
            label={t('input.label.censorious')}
            options={getSelectOptions(TRACK_CENSORIOUS, 'enum.TRACK_CENSORIOUS')}
          />

          <FormInput.Select
            name="genre.id"
            label={t('input.label.genre')}
            options={
              genres.data?.map((genre) => ({
                value: genre.id,
                label: genre.name,
              })) ?? []
            }
            isLoading={genres.isLoading}
            value={{
              value: values.genre.id,
              label: values.genre.name,
            }}
            onChange={(option) => {
              if (option) {
                setFieldValue('genre', {
                  id: option.value,
                  name: option.label,
                });
              }
            }}
          />
          <FormInput.Select
            options={boolOptions}
            label={t('input.label.isCreatedWithAi')}
            name="isCreatedWithAi"
            onChange={(option) => {
              setFieldValue('isCreatedWithAi', option.value ? true : false);
            }}
            isClearable={false}
          />
          <FormInput.Select
            options={boolOptions}
            label={t('input.label.instrumental')}
            name="isInstrumental"
            onChange={(option) => {
              setFieldValue('isInstrumental', option.value ? true : false);
            }}
            isClearable={false}
          />
          <Input.ComplexWrapper sizes={[40, 60]} className="w-full">
            <FormInput.Select
              name="pline.year"
              label={`${t('input.label.year')}`}
              placeholder=""
              options={getYearsSelectOptions()}
              isCreatable
              titleEnabled
            />
            <FormInput.Select
              name="pline.name"
              label={`${t('input.label.pline')}`}
              options={copyrightOptions.data}
              isCreatable
              titleEnabled
              placeholder=""
              isLoading={copyrightOptions.isLoading}
            />
          </Input.ComplexWrapper>
          <Input.ComplexWrapper sizes={[40, 60]} className="w-full">
            <FormInput.Select
              name="cline.year"
              label={`${t('input.label.year')}`}
              placeholder=""
              options={getYearsSelectOptions()}
              isCreatable
              titleEnabled
            />
            <FormInput.Select
              name="cline.name"
              label={`${t('input.label.cline')}`}
              options={copyrightOptions.data}
              isCreatable
              titleEnabled
              placeholder=""
              isLoading={copyrightOptions.isLoading}
            />
          </Input.ComplexWrapper>
        </Container.Content>
      </Container>
    </DataWrapper>
  );
};

export default VideoData;
